import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addEventDate, fetchEventDetails, mergeEventDate, updateEvent } from '../../store/slices/eventSlice';
import { useNavigate, useParams } from 'react-router-dom';
import axios from '../../utils/axiosConfig';
import {
  Calendar,
  MapPin,
  DollarSign,
  Users,
  Image as ImageIcon,
  ChevronLeft,
  XCircle,
  Plus,
  Save,
  Loader2,
  Map,
  Globe,
  Building2,
  Info,
  ChevronRight,
  Clock,
  AlertTriangle,
  Ticket
} from 'lucide-react';

const InputGroup = ({ label, icon, children }) => (
  <div className="space-y-2">
    <label className="text-[10px] font-black text-white/30 uppercase tracking-[2px] ml-1 flex items-center gap-2">
      {icon}
      {label}
    </label>
    {children}
  </div>
);

const MIN_EVENT_TICKETS = 10;

const normalizeNonNegativeInput = (value) => {
  if (value === '') return '';
  return String(Math.max(0, Number(value) || 0));
};

const getEventDates = (event) => {
  const rawDates = Array.isArray(event?.dates) && event.dates.length > 0 ? event.dates : [event?.date];
  return [...new Set(rawDates.filter(Boolean).map((value) => new Date(value).toISOString().slice(0, 10)))].sort();
};

const EditEvent = () => {
  const { slug, id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventDetails, loading, error } = useSelector((state) => state.events);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState('');
  const [additionalDate, setAdditionalDate] = useState('');
  const [additionalDates, setAdditionalDates] = useState([]);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [allDay, setAllDay] = useState(false);
  const [salesMode, setSalesMode] = useState('tickets');
  const [bookingAmount, setBookingAmount] = useState('');
  const [bookingPaidAmount, setBookingPaidAmount] = useState('');
  const [bookingPaymentStatus, setBookingPaymentStatus] = useState('pending');
  const [bookingNotes, setBookingNotes] = useState('');
  const [price, setPrice] = useState('');
  const [totalTickets, setTotalTickets] = useState('');
  const [expenses, setExpenses] = useState('');
  const [isPublic, setIsPublic] = useState(true);

  // Dirección estructurada
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [province, setProvince] = useState('');
  const [country, setCountry] = useState('Argentina');

  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [salon, setSalon] = useState(null);
  const [loadingSalon, setLoadingSalon] = useState(true);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [addingDate, setAddingDate] = useState(false);

  const refreshEventDetails = useCallback(() => {
    if (slug && id) dispatch(fetchEventDetails({ slug, id }));
  }, [dispatch, slug, id]);

  const fetchSalon = useCallback(async () => {
    try {
      const { data } = await axios.get(`/api/clients/${slug}`);
      setSalon(data);
    } catch (err) {
      console.error('Error fetching salon:', err);
    } finally {
      setLoadingSalon(false);
    }
  }, [slug]);

  useEffect(() => {
    refreshEventDetails();
    fetchSalon();
  }, [fetchSalon, refreshEventDetails]);

  useEffect(() => {
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshEventDetails();
      }
    };

    const interval = window.setInterval(refreshWhenVisible, 15000);
    window.addEventListener('focus', refreshEventDetails);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshEventDetails);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [refreshEventDetails]);

  const isMpConnected = !!salon?.hasMpConnected;
  const isBookingMode = salesMode === 'booking';
  const isMpRequired = !isBookingMode && salon?.billingModel !== 'subscription' && !isMpConnected;
  const isSubscriptionClient = salon?.billingModel === 'subscription';

  useEffect(() => {
    if (eventDetails) {
      setTitle(eventDetails.title || '');
      setDescription(eventDetails.description || '');
      const eventDates = getEventDates(eventDetails);
      const primaryDate = eventDetails.date ? new Date(eventDetails.date).toISOString().split('T')[0] : eventDates[0] || '';
      setDate(primaryDate);
      setAdditionalDates(eventDates.filter((eventDate) => eventDate !== primaryDate));
      setStartTime(eventDetails.startTime || '');
      setEndTime(eventDetails.endTime || '');
      setAllDay(Boolean(eventDetails.allDay || !eventDetails.startTime || !eventDetails.endTime));
      setSalesMode(eventDetails.salesMode || 'tickets');
      setPrice(eventDetails.price || '');
      setTotalTickets(eventDetails.totalTickets || '');
      setExpenses(eventDetails.expenses ?? 0);
      setBookingAmount(eventDetails.bookingAmount ?? '');
      setBookingPaidAmount(eventDetails.bookingPaidAmount ?? '');
      setBookingPaymentStatus(eventDetails.bookingPaymentStatus || 'pending');
      setBookingNotes(eventDetails.bookingNotes || '');
      setIsPublic(eventDetails.isPublic ?? true);
      setAddress(eventDetails.address || '');
      setCity(eventDetails.city || '');
      setProvince(eventDetails.province || '');
      setCountry(eventDetails.country || 'Argentina');
      setCoverPreview(eventDetails.coverImage || '');
    }
  }, [eventDetails]);

  useEffect(() => {
    if (isSubscriptionClient) {
      setIsPublic(false);
    }
  }, [isSubscriptionClient]);

  useEffect(() => {
    if (allDay) {
      setStartTime('');
      setEndTime('');
    }
  }, [allDay]);

  useEffect(() => {
    if (!date || (!allDay && (!startTime || !endTime))) {
      setAvailability(null);
      return;
    }

    let alive = true;
    const timer = window.setTimeout(async () => {
      setCheckingAvailability(true);
      try {
        const { data } = await axios.get(`/${slug}/api/events/availability`, {
          params: { date, startTime, endTime, allDay, excludeEventId: id },
        });
        if (alive) setAvailability(data);
      } catch (err) {
        if (alive) setAvailability(null);
      } finally {
        if (alive) setCheckingAvailability(false);
      }
    }, 350);

    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  }, [slug, id, date, startTime, endTime, allDay]);

  const onPickCover = (e) => {
    const f = e.target.files?.[0];
    setCoverFile(f || null);
    setCoverPreview(f ? URL.createObjectURL(f) : eventDetails?.coverImage || '');
  };

  const removeCover = () => {
    setCoverFile(null);
    setCoverPreview('');
  };

  const handleAddAdditionalDate = async () => {
    if (!additionalDate) return;
    if (additionalDate === date) {
      alert('El evento ya esta reservado para esa fecha.');
      return;
    }

    setAddingDate(true);
    try {
      try {
        await dispatch(addEventDate({ slug, id, date: additionalDate })).unwrap();
      } catch (error) {
        if (error?.code !== 'SCHEDULE_CONFLICT') throw error;

        const duplicate = (error.conflicts || []).find((event) => event.title?.trim() === title.trim());
        const conflictText = (error.conflicts || []).map((event) => `- ${event.dateOnly ? `${event.dateOnly} - ` : ''}${event.title}`).join('\n');
        if (duplicate) {
          const confirmedMerge = window.confirm(
            `Ya existe una reserva con este mismo nombre el ${duplicate.dateOnly}.\n\nQueres integrarla a este evento para que quede una sola gestion?`
          );
          if (!confirmedMerge) return;
          await dispatch(mergeEventDate({ slug, id, eventId: duplicate._id })).unwrap();
          setAdditionalDate('');
          setSuccessMessage('La fecha existente se integro al evento.');
          window.setTimeout(() => setSuccessMessage(''), 2500);
          return;
        }
        const confirmed = window.confirm(
          `Esta fecha ya tiene una reserva:\n${conflictText}\n\nQueres agregar este dia igual?`
        );
        if (!confirmed) return;
        await dispatch(addEventDate({ slug, id, date: additionalDate, allowScheduleConflict: true })).unwrap();
      }

      setAdditionalDate('');
      setSuccessMessage('Dia agregado a la agenda.');
      window.setTimeout(() => setSuccessMessage(''), 2500);
    } catch (err) {
      console.error('Error al agregar fecha:', err);
      alert(err.error || err.message || 'No se pudo agregar la fecha.');
    } finally {
      setAddingDate(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMessage('');

    const formData = new FormData();
    formData.append('title', title);
    formData.append('description', description);
    formData.append('date', date);
    formData.append('startTime', startTime);
    formData.append('endTime', endTime);
    formData.append('allDay', allDay);
    formData.append('salesMode', salesMode);
    formData.append('address', address);
    formData.append('city', city);
    formData.append('province', province);
    formData.append('country', country);
    formData.append('location', `${address}, ${city}`);
    formData.append('price', isBookingMode ? 0 : price);
    formData.append('totalTickets', isBookingMode ? 0 : Math.max(MIN_EVENT_TICKETS, Number(totalTickets) || 0));
    formData.append('expenses', normalizeNonNegativeInput(expenses));
    formData.append('bookingAmount', normalizeNonNegativeInput(bookingAmount));
    formData.append('bookingPaidAmount', normalizeNonNegativeInput(bookingPaidAmount));
    formData.append('bookingPaymentStatus', bookingPaymentStatus);
    formData.append('bookingNotes', bookingNotes);
    formData.append('isPublic', isBookingMode || isSubscriptionClient ? false : isPublic);

    if (coverFile) {
      formData.append('cover', coverFile);
    }

    try {
      try {
        await dispatch(updateEvent({ slug, id, data: formData })).unwrap();
      } catch (error) {
        if (error?.code !== 'SCHEDULE_CONFLICT') throw error;

        const conflictText = (error.conflicts || [])
          .map((event) => `- ${event.title} (${event.allDay ? 'dia completo' : `${event.startTime || '--'}-${event.endTime || '--'}`})`)
          .join('\n');
        const confirmed = window.confirm(
          `Esta fecha u horario ya esta ocupado:\n${conflictText}\n\nQueres guardar el cambio igual?`
        );

        if (!confirmed) throw error;
        formData.append('allowScheduleConflict', 'true');
        await dispatch(updateEvent({ slug, id, data: formData })).unwrap();
      }

      setSuccessMessage('Evento actualizado con éxito.');
      setTimeout(() => {
        setSuccessMessage('');
        navigate(`/${slug}/admin/events`);
      }, 1500);
    } catch (err) {
      console.error('Error al actualizar evento:', err);
      alert(err.error || err.message || 'Error al actualizar el evento.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !eventDetails) {
    return (
      <div className="min-h-screen bg-[#080808] flex items-center justify-center">
        <Loader2 className="animate-spin text-primary-orange" size={48} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#080808] text-white selection:bg-primary-orange/30 pb-20 overflow-x-hidden">
      {/* Glow Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[20%] right-[-10%] w-[50%] h-[50%] bg-primary-orange/5 rounded-full blur-[140px] animate-glow" />
        <div className="absolute bottom-[10%] left-[-10%] w-[40%] h-[40%] bg-primary-red/5 rounded-full blur-[120px] animate-glow-delayed" />
      </div>

      <div className="max-w-5xl mx-auto px-6 pt-12 relative z-10">
        <button
          onClick={() => navigate(`/${slug}/admin/events`)}
          className="flex items-center gap-2 text-white/40 hover:text-white transition-colors mb-8 group"
        >
          <ChevronLeft size={20} className="transition-transform group-hover:-translate-x-1" />
          <span className="font-black text-[10px] uppercase tracking-[2px]">Volver a eventos</span>
        </button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
          <div>
            <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-2 uppercase">Editar <span className="text-gradient">Evento</span></h1>
            <p className="text-white/40 font-medium tracking-tight">Actualizá la información para que tus seguidores estén al tanto.</p>
          </div>
          {successMessage && (
            <div className="bg-green-500/10 border border-green-500/20 text-green-400 px-6 py-3 rounded-2xl font-bold animate-in fade-in zoom-in duration-300">
              {successMessage}
            </div>
          )}
        </div>

        {!loadingSalon && isMpRequired && (
          <div className="mb-12 bg-primary-orange/10 border border-primary-orange/20 p-8 rounded-[40px] flex flex-col md:flex-row items-center gap-6 animate-fade-in-up">
            <div className="w-16 h-16 bg-primary-orange/20 rounded-3xl flex items-center justify-center text-primary-orange shrink-0">
              <XCircle size={32} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="text-xl font-black uppercase text-primary-orange mb-1">Mercado Pago no conectado</h3>
              <p className="text-white/60 font-medium leading-relaxed">
                Para poder vender entradas y cobrar al instante, necesitás vincular tu cuenta de Mercado Pago.
                Sin esto, no podés guardar cambios en tus eventos.
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate(`/${slug}/admin/settings?focus=mp`)}
              className="px-8 py-4 bg-primary-orange text-black font-black rounded-2xl hover:scale-105 transition-all shadow-lg shadow-orange-500/20"
            >
              CONECTAR AHORA
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-8 space-y-8">
            {/* Info */}
            <div className="bg-glass rounded-[40px] p-8 md:p-10 shadow-3xl space-y-8">
              <InputGroup label="Título del Evento" icon={<Info size={12} className="text-primary-orange" />}>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-lg font-black placeholder:text-white/10 focus:border-primary-orange/50 transition-all outline-none"
                  required
                />
              </InputGroup>

              <InputGroup label="Descripción" icon={<Plus size={12} className="text-primary-orange" />}>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-medium placeholder:text-white/10 focus:border-primary-orange/50 transition-all h-48 resize-none outline-none"
                  required
                />
              </InputGroup>

              <InputGroup label="Modalidad" icon={<Ticket size={12} className="text-primary-orange" />}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setSalesMode('tickets')}
                    className={`text-left rounded-2xl border p-5 transition-all ${salesMode === 'tickets' ? 'bg-primary-orange/10 border-primary-orange/40 text-white' : 'bg-white/5 border-white/10 text-white/55 hover:bg-white/10'}`}
                  >
                    <p className="text-sm font-black">Vender entradas online</p>
                    <p className="text-[10px] font-medium mt-2 leading-relaxed">Usa stock, Mercado Pago, QR y control de accesos.</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSalesMode('booking');
                      setIsPublic(false);
                    }}
                    className={`text-left rounded-2xl border p-5 transition-all ${salesMode === 'booking' ? 'bg-blue-500/10 border-blue-500/40 text-white' : 'bg-white/5 border-white/10 text-white/55 hover:bg-white/10'}`}
                  >
                    <p className="text-sm font-black">Solo agenda y cobros</p>
                    <p className="text-[10px] font-medium mt-2 leading-relaxed">Reserva la fecha, registra seña y saldo sin publicar tickets.</p>
                  </button>
                </div>
              </InputGroup>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup label="Fecha" icon={<Calendar size={12} className="text-primary-orange" />}>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50"
                    required
                  />
                </InputGroup>

                <InputGroup label="Sumar dia" icon={<Plus size={12} className="text-primary-orange" />}>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      value={additionalDate}
                      onChange={(e) => setAdditionalDate(e.target.value)}
                      className="min-w-0 flex-1 bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50"
                    />
                    <button
                      type="button"
                      onClick={handleAddAdditionalDate}
                      disabled={!additionalDate || addingDate}
                      className="shrink-0 rounded-2xl border border-primary-orange/30 bg-primary-orange/10 px-4 text-primary-orange font-black text-xs hover:bg-primary-orange hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {addingDate ? 'AGREGANDO...' : 'AGREGAR'}
                    </button>
                  </div>
                  <p className="text-[10px] text-white/40 mt-2">Agrega esta fecha al mismo evento, con una sola gestion de cobros, invitados y accesos.</p>
                  {additionalDates.length > 0 && (
                    <div className="flex flex-wrap gap-2 pt-2">
                      {additionalDates.map((eventDate) => (
                        <span key={eventDate} className="rounded-xl border border-primary-orange/20 bg-primary-orange/10 px-3 py-2 text-[10px] font-black text-primary-orange">
                          {eventDate}
                        </span>
                      ))}
                    </div>
                  )}
                </InputGroup>

                <div className="grid grid-cols-2 gap-4">
                  <InputGroup label="Apertura" icon={<Clock size={12} className="text-primary-orange" />}>
                    <input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      disabled={allDay}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-primary-orange/50 transition-all outline-none disabled:opacity-40"
                    />
                  </InputGroup>
                  <InputGroup label="Cierre" icon={<Clock size={12} className="text-primary-orange" />}>
                    <input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      disabled={allDay}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black focus:border-primary-orange/50 transition-all outline-none disabled:opacity-40"
                    />
                  </InputGroup>
                </div>

                {!isBookingMode && (
                <InputGroup label="Precio ($)" icon={<DollarSign size={12} className="text-primary-orange" />}>
                  <input
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50"
                    required
                  />
                </InputGroup>
                )}
              </div>

              <div className="space-y-3">
                <label className="flex items-center justify-between gap-4 bg-white/5 border border-white/10 rounded-2xl p-5 cursor-pointer">
                  <div>
                    <p className="text-sm font-black text-white">Reservar dia completo</p>
                    <p className="text-[10px] text-white/40 mt-1">Bloquea toda la fecha en la agenda del salon.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={allDay}
                    onChange={(e) => setAllDay(e.target.checked)}
                    className="h-5 w-5 accent-primary-orange"
                  />
                </label>

                {(checkingAvailability || availability) && (
                  <div className={`rounded-2xl border p-4 ${availability?.available ? 'bg-green-500/10 border-green-500/20 text-green-300' : 'bg-primary-orange/10 border-primary-orange/25 text-primary-orange'}`}>
                    <div className="flex items-start gap-3">
                      <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-black">
                          {checkingAvailability
                            ? 'Consultando disponibilidad...'
                            : availability?.available
                              ? 'Fecha disponible'
                              : 'Fecha u horario ocupado'}
                        </p>
                        {!checkingAvailability && !availability?.available && availability?.conflicts?.length > 0 && (
                          <p className="text-xs font-medium mt-1 text-white/60">
                            {availability.conflicts.map((event) => event.title).join(', ')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Ubicación */}
            <div className="bg-glass rounded-[40px] p-8 md:p-10 shadow-3xl space-y-8">
              <div className="flex items-center gap-4 mb-2">
                <div className="w-12 h-12 bg-brand-gradient rounded-2xl flex items-center justify-center shadow-lg shadow-orange-500/20">
                  <MapPin size={24} />
                </div>
                <div>
                  <h3 className="text-xl font-black uppercase">Ubicación</h3>
                  <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px]">Editá el lugar del encuentro</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <InputGroup label="Dirección" icon={<Map size={12} className="text-primary-orange" />}>
                  <input
                    type="text"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50"
                    required
                  />
                </InputGroup>
                <InputGroup label="Ciudad" icon={<Building2 size={12} className="text-primary-orange" />}>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50"
                    required
                  />
                </InputGroup>
                <InputGroup label="Provincia" icon={<MapPin size={12} className="text-primary-orange" />}>
                  <select
                    value={province}
                    onChange={(e) => setProvince(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black outline-none focus:border-primary-orange/50 appearance-none"
                    required
                  >
                    {[
                      'Buenos Aires', 'CABA', 'Catamarca', 'Chaco', 'Chubut', 'Córdoba', 'Corrientes', 'Entre Ríos',
                      'Formosa', 'Jujuy', 'La Pampa', 'La Rioja', 'Mendoza', 'Misiones', 'Neuquén', 'Río Negro',
                      'Salta', 'San Juan', 'San Luis', 'Santa Cruz', 'Santa Fe', 'Santiago del Estero', 'Tierra del Fuego', 'Tucumán'
                    ].map(p => (
                      <option key={p} value={p} className="bg-[#080808]">{p}</option>
                    ))}
                  </select>
                </InputGroup>
                <InputGroup label="País" icon={<Globe size={12} className="text-primary-orange" />}>
                  <input
                    type="text"
                    disabled
                    value={country}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white/40 font-black"
                  />
                </InputGroup>
              </div>
            </div>
          </div>

          <div className="lg:col-span-4 space-y-8">
            {/* Imagen */}
            <div className="bg-glass rounded-[40px] p-8 shadow-3xl">
              <InputGroup label="Imagen de Portada" icon={<ImageIcon size={12} className="text-primary-orange" />}>
                <div className="relative group/cover aspect-video bg-white/5 border-2 border-dashed border-white/10 rounded-3xl overflow-hidden mt-2">
                  {coverPreview ? (
                    <>
                      <img src={coverPreview} alt="Preview" className="w-full h-full object-cover group-hover/cover:scale-110 transition-transform duration-700" />
                      <button
                        type="button"
                        onClick={removeCover}
                        className="absolute top-3 right-3 p-2 bg-black/60 backdrop-blur-md rounded-full text-red-400 hover:scale-110 transition-all border border-white/10"
                      >
                        <XCircle size={20} />
                      </button>
                    </>
                  ) : (
                    <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-white/[0.02] transition-colors">
                      <div className="text-white/20 font-black">+</div>
                      <span className="text-[10px] font-black uppercase tracking-[2px] text-white/20">Cambiar Imagen</span>
                      <input type="file" className="hidden" accept="image/*" onChange={onPickCover} />
                    </label>
                  )}
                </div>
              </InputGroup>
            </div>

            {/* Tickets */}
            <div className="bg-glass rounded-[40px] p-8 shadow-3xl space-y-8">
              {isBookingMode ? (
                <div className="space-y-5">
                  <div className="p-5 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                    <h4 className="text-sm font-black text-blue-300">Reserva privada</h4>
                    <p className="text-[10px] text-white/50 mt-2 leading-relaxed">
                      Este evento solo bloquea agenda y registra cobros internos. No se publican entradas online.
                    </p>
                  </div>
                  <InputGroup label="Monto acordado" icon={<DollarSign size={12} className="text-primary-orange" />}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={bookingAmount}
                      onChange={(e) => setBookingAmount(e.target.value)}
                      onBlur={(e) => setBookingAmount(normalizeNonNegativeInput(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black"
                    />
                  </InputGroup>
                  <InputGroup label="Seña / cobrado" icon={<DollarSign size={12} className="text-primary-orange" />}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={bookingPaidAmount}
                      onChange={(e) => setBookingPaidAmount(e.target.value)}
                      onBlur={(e) => setBookingPaidAmount(normalizeNonNegativeInput(e.target.value))}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black"
                    />
                  </InputGroup>
                  <InputGroup label="Estado de cobro" icon={<Info size={12} className="text-primary-orange" />}>
                    <select
                      value={bookingPaymentStatus}
                      onChange={(e) => setBookingPaymentStatus(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black"
                    >
                      <option value="pending" className="bg-[#080808]">Pendiente</option>
                      <option value="partial" className="bg-[#080808]">Seña parcial</option>
                      <option value="paid" className="bg-[#080808]">Cobrado completo</option>
                    </select>
                  </InputGroup>
                  <InputGroup label="Notas internas" icon={<Plus size={12} className="text-primary-orange" />}>
                    <textarea
                      value={bookingNotes}
                      onChange={(e) => setBookingNotes(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-medium h-28 resize-none"
                    />
                  </InputGroup>
                </div>
              ) : (
              <>
              <div className="bg-white/5 p-4 rounded-2xl border border-white/5">
                <p className="text-[10px] font-black text-white/20 uppercase tracking-[2px] mb-2">Ventas Actuales</p>
                <p className="text-3xl font-black text-primary-orange">{eventDetails?.soldTickets || 0} <span className="text-sm text-white/40 font-medium">vendidas</span></p>
              </div>

              <InputGroup label="Entradas Totales" icon={<Users size={12} className="text-primary-orange" />}>
                <input
                  type="number"
                  value={totalTickets}
                  onChange={(e) => setTotalTickets(e.target.value)}
                  min={MIN_EVENT_TICKETS}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black"
                  required
                />
              </InputGroup>
              </>
              )}

              <InputGroup label="Gastos Estimados" icon={<DollarSign size={12} className="text-primary-orange" />}>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={expenses}
                  onChange={(e) => setExpenses(e.target.value)}
                  onBlur={(e) => setExpenses(normalizeNonNegativeInput(e.target.value))}
                  className="w-full bg-white/5 border border-white/10 rounded-2xl p-5 text-white font-black"
                />
              </InputGroup>

              {/* Toggle Public / Private */}
              {isBookingMode ? (
                <div className="p-5 bg-blue-500/5 border border-blue-500/20 rounded-2xl">
                  <h4 className="text-sm font-black text-blue-300">No publicado</h4>
                  <p className="text-[10px] text-white/50 mt-2 leading-relaxed">
                    Las reservas solo aparecen en tu agenda y en la administracion del salon.
                  </p>
                </div>
              ) : isSubscriptionClient ? (
                <div className="p-5 bg-primary-orange/5 border border-primary-orange/20 rounded-2xl">
                  <h4 className="text-sm font-black text-primary-orange">Evento por invitación</h4>
                  <p className="text-[10px] text-white/50 mt-2 leading-relaxed">
                    En salones suscriptos no se habilita la venta pública. Este evento queda privado y las entradas se emiten una por una desde la gestión del salón.
                  </p>
                </div>
              ) : (
                <div className="flex items-center justify-between p-5 bg-white/5 border border-white/10 rounded-2xl">
                  <div>
                    <h4 className="text-sm font-black text-white">Evento Público</h4>
                    <p className="text-[10px] text-white/40 mt-1">Si está activo, el evento aparece en la portada. Desactivalo para que sea totalmente privado (solo visible en tu perfil).</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer ml-4">
                    <input
                      type="checkbox"
                      checked={isPublic}
                      onChange={(e) => setIsPublic(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-14 h-7 bg-white/10 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-orange"></div>
                  </label>
                </div>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={saving || (!loadingSalon && isMpRequired)}
              className="group w-full bg-brand-gradient py-6 rounded-[32px] font-black text-white text-lg shadow-xl shadow-orange-500/20 hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:grayscale disabled:cursor-not-allowed flex items-center justify-center gap-3 shine-effect"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={24} />
                  GUARDANDO...
                </>
              ) : isMpRequired && !loadingSalon ? (
                <>
                  <XCircle size={24} />
                  MP REQUERIDO
                </>
              ) : (
                <>
                  <Save size={24} />
                  GUARDAR CAMBIOS
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEvent;
